import React, { useState, useEffect } from 'react';
import { Modal, Button, Form, Spinner, Alert, Row, Col, Card } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { getRolePermissions, updateRolePermissions, resetRolePermissions } from '../services/api';
import { PERMISSION_GROUPS, PERMISSION_LABELS } from '../utils/permissions';
import toast from 'react-hot-toast';

const RolePermissionsModal = ({ show, onHide, role, roleName }) => {
    const { t } = useTranslation();
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [permissions, setPermissions] = useState([]);
    const [isCustom, setIsCustom] = useState(false);
    const [error, setError] = useState('');

    useEffect(() => {
        if (show && role) {
            fetchPermissions();
        }
    }, [show, role]);

    const fetchPermissions = async () => {
        setLoading(true);
        setError('');
        try {
            const response = await getRolePermissions(role);
            setPermissions(response.data.permissions || []);
            setIsCustom(response.data.isCustom);
        } catch (err) {
            console.error('Error fetching permissions:', err);
            setError(t('rolePermissions.fetchError'));
        } finally {
            setLoading(false);
        }
    };

    const handlePermissionToggle = (permission) => {
        setPermissions(prev => {
            if (prev.includes(permission)) {
                return prev.filter(p => p !== permission);
            } else {
                return [...prev, permission];
            }
        });
    };

    const handleToggleGroup = (groupPermissions, checked) => {
        setPermissions(prev => {
            if (checked) {
                // Add all group permissions
                const newPerms = [...prev];
                groupPermissions.forEach(p => {
                    if (!newPerms.includes(p)) {
                        newPerms.push(p);
                    }
                });
                return newPerms;
            } else {
                // Remove all group permissions
                return prev.filter(p => !groupPermissions.includes(p));
            }
        });
    };

    const handleSave = async () => {
        setSaving(true);
        try {
            await updateRolePermissions(role, permissions);
            toast.success(t('rolePermissions.saved'));
            onHide(true); // Pass true to indicate changes were made
        } catch (err) {
            console.error('Error saving permissions:', err);
            toast.error(t('rolePermissions.saveError'));
        } finally {
            setSaving(false);
        }
    };

    const handleReset = async () => {
        if (!window.confirm(t('rolePermissions.resetConfirm'))) return;

        setSaving(true);
        try {
            const response = await resetRolePermissions(role);
            setPermissions(response.data.permissions || []);
            setIsCustom(false);
            toast.success(t('rolePermissions.reset'));
        } catch (err) {
            console.error('Error resetting permissions:', err);
            toast.error(t('rolePermissions.resetError'));
        } finally {
            setSaving(false);
        }
    };

    const isGroupChecked = (groupPermissions) => {
        return groupPermissions.every(p => permissions.includes(p));
    };

    const isGroupPartial = (groupPermissions) => {
        const checked = groupPermissions.filter(p => permissions.includes(p)).length;
        return checked > 0 && checked < groupPermissions.length;
    };

    return (
        <Modal show={show} onHide={() => onHide(false)} size="lg" centered backdrop="static">
            <Modal.Header closeButton>
                <Modal.Title>
                    {t('rolePermissions.title', { role: roleName })}
                </Modal.Title>
            </Modal.Header>
            <Modal.Body>
                {error && <Alert variant="danger">{error}</Alert>}

                {loading ? (
                    <div className="text-center py-4">
                        <Spinner animation="border" />
                    </div>
                ) : (
                    <>
                        {isCustom && (
                            <Alert variant="info" className="mb-3">
                                {t('rolePermissions.customized')}
                            </Alert>
                        )}

                        <Row>
                            {Object.entries(PERMISSION_GROUPS).map(([groupKey, group]) => (
                                <Col md={6} key={groupKey} className="mb-3">
                                    <Card className="h-100">
                                        <Card.Header className="py-2">
                                            <Form.Check
                                                type="checkbox"
                                                label={<strong>{t(group.labelKey)}</strong>}
                                                checked={isGroupChecked(group.permissions)}
                                                ref={input => {
                                                    if (input) {
                                                        input.indeterminate = isGroupPartial(group.permissions);
                                                    }
                                                }}
                                                onChange={(e) => handleToggleGroup(group.permissions, e.target.checked)}
                                            />
                                        </Card.Header>
                                        <Card.Body className="py-2">
                                            {group.permissions.map(permission => (
                                                <Form.Check
                                                    key={permission}
                                                    type="checkbox"
                                                    id={`perm-${permission}`}
                                                    label={t(PERMISSION_LABELS[permission] || permission)}
                                                    checked={permissions.includes(permission)}
                                                    onChange={() => handlePermissionToggle(permission)}
                                                    className="mb-1 ms-3"
                                                />
                                            ))}
                                        </Card.Body>
                                    </Card>
                                </Col>
                            ))}
                        </Row>
                    </>
                )}
            </Modal.Body>
            <Modal.Footer>
                {isCustom && (
                    <Button variant="outline-secondary" onClick={handleReset} disabled={saving}>
                        {t('rolePermissions.resetButton')}
                    </Button>
                )}
                <div className="flex-grow-1"></div>
                <Button variant="secondary" onClick={() => onHide(false)}>
                    {t('cancel')}
                </Button>
                <Button variant="primary" onClick={handleSave} disabled={saving || loading}>
                    {saving ? <Spinner animation="border" size="sm" /> : t('save')}
                </Button>
            </Modal.Footer>
        </Modal>
    );
};

export default RolePermissionsModal;
